import { Observable, Subscription } from "rxjs";

import { Component, Input, OnInit, ChangeDetectorRef, OnDestroy } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";

import * as _ from 'lodash';

import { PagedListAdapter } from "../../../../common/paged-list-adapter";
import { Call } from "../../../../models/call";

import swal from "bootstrap-sweetalert";
import { PagedResult } from "app/models/PagedResult";
import { Work } from "app/models/work";
import { ProfileService } from "app/services/profile.service";
import { ScrollToAnchorService } from "app/services/scroll-to-anchor.service";
import { DomSanitizer } from "@angular/platform-browser";

declare const $: any;

@Component({
  selector: 'call-list',
  styleUrls: ['./call-list.component.scss'],
  templateUrl: './call-list.component.html'
})
export class CallListComponent implements OnInit, OnDestroy {
  private _itemsSubscription: Subscription;
  private _itemsObservable: Observable<PagedResult<Call>>;
  private _items: Call[] = null;
  private callConfirmationPermission: boolean

  private _pagination = {
    id: 'items_pagination',
    itemsPerPage: 20,
    currentPage: 1,
    totalItems: 0
  };

  private _sortBy = "Number";
  private _isDesc = false;

  private _errorState: CallListComponent.ErrorState = CallListComponent.ErrorState.None;

  constructor(
    private _changeDetector: ChangeDetectorRef,
    private activatedRoute: ActivatedRoute,
    private _profileService: ProfileService,
    private router: Router,
    private _scrollToAnchorService: ScrollToAnchorService,
    public sanitizer: DomSanitizer) {
    this.callConfirmationPermission = this._profileService.checkHasCallConfirmationPermission();
  }

  ngOnInit(): void {
    if (this.initialItemsPerPage != null)
      this._pagination.itemsPerPage = this.initialItemsPerPage;

    this.activatedRoute.queryParams.subscribe(params => {
      this._isDesc = params['isDesc'] ? JSON.parse(params['isDesc']) : true;
      this._sortBy = params['sortBy'] ? params['sortBy'] : "CreatedInFSM";
      this._pagination.itemsPerPage = params['count'] ? params['count'] : 10;
      this._pagination.currentPage = params['page'] ? params['page'] : 1;

      this.clearSubscription();

      this.loadCalls();
    });
  }

  ngOnDestroy(): void {
    this.clearSubscription();
    swal.close();
  }

  @Input("adapter")
  public adapter: PagedListAdapter<Call>;

  @Input("initialItemsPerPage")
  public initialItemsPerPage?: number = null;

  private sort(sortBy: string, isDesc: boolean) {
    this.setQueryParams(
      {
        sortBy: sortBy,
        isDesc: isDesc
      });
  }

  private setItemsPerPage(itemsPerPage: number): void {
    var firstPageItemIndex = this._pagination.itemsPerPage * (this._pagination.currentPage - 1);
    this.setQueryParams(
      {
        page: Math.floor(firstPageItemIndex / this._pagination.itemsPerPage) + 1,
        count: itemsPerPage
      });
  }

  private setPage(page: number) {
    this.setQueryParams({ page: page });
  }

  private setQueryParams(queryParams: object) {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        ...this.activatedRoute.snapshot.queryParams,
        ...queryParams
      }
    });
  }

  private loadCalls(): void {
    this._errorState = CallListComponent.ErrorState.None;
    // WORKAROUND: без этой строчки изменение поля this._errorState не определяется в разметке
    this._changeDetector.markForCheck();

    this.clearSubscription();

    let itemsObservable = this.adapter.getItems(this._pagination.itemsPerPage, (this._pagination.currentPage - 1) * this._pagination.itemsPerPage, this._sortBy, this._isDesc);
    this._itemsObservable = itemsObservable;

    this._itemsSubscription = itemsObservable
      .subscribe(x => {
        // если на форме сейчас те элементы, которыми управляет данный Observable
        if (this._itemsObservable === itemsObservable) {
          this._items = x.items;

          // преобразуем html в текст
          this._items.forEach(element => {
            element.description = $("<div>").html(element.description).text();
          });

          this._pagination.totalItems = x.totalCount;
          this._changeDetector.markForCheck();
          this.restoreScrollPosition();
        }
      }, err => {
        // если на форме сейчас те элементы, которыми управляет данный Observable
        if (this._itemsObservable === itemsObservable) {
          this._errorState = CallListComponent.ErrorState.UnknownError;
          // WORKAROUND: без этой строчки изменение поля this._errorState не определяется в разметке
          this._changeDetector.markForCheck();

          swal({
            title: 'Ошибка',
            text: 'Не удалось получить данные обращений. Попробуйте снова',
            type: 'error',
            confirmButtonText: 'Попробовать еще раз'
          }, () => {
            this.loadCalls();
          });
        }
      });
  }

  private convertCallStatusToText(status: Call.Status): string {
    switch (status) {
      case Call.Status.Opened:
        return "Открыто";
      case Call.Status.Closed:
        return "Закрыто";
      default:
        return "";
    }
  }

  private getObjectString(works: Work[]): string {
    var serviceObjects = _.map(works, 'serviceObject');
    return _.map(serviceObjects, 'name').join('; ');
  }

  private getColorStatus(status: Call.Status): string {
    switch (status) {
      case Call.Status.Opened: return "label-success";
      case Call.Status.Closed: return "label-warning";
      default: return "label-default";
    }
  }

  private getColorCancelStatus(status: Call.CancelStatus): string {
    switch (status) {
      case Call.CancelStatus.NotRequested: return "label-transparent"
      case Call.CancelStatus.Requested: return "label-default label-transparent"
      case Call.CancelStatus.Successful: return "label-success"
      case Call.CancelStatus.Unsuccessful: return "label-warning"
    }
  }

  private convertCancelStatusToText(status: Call.CancelStatus): string {
    switch (status) {
      case Call.CancelStatus.NotRequested: return "-"
      case Call.CancelStatus.Requested: return "Отправлен запрос"
      case Call.CancelStatus.Successful: return "Отменено"
      case Call.CancelStatus.Unsuccessful: return "Отмена невозможна"
    }
  }

  private clearSubscription() {
    if (this._itemsSubscription)
      this._itemsSubscription.unsubscribe();

    this._itemsSubscription = null;
    this._itemsObservable = null;
    this._items = null;
  }

  private goToDetailCall(elementId : string, callId : number) {
    let currentUrl = this.router.url.split('#')[0];
    this.router.navigate([`/call/${callId}`]);
    window.history.replaceState({}, '', currentUrl + "#" + elementId);
  }

  private restoreScrollPosition() : void {
    const fragment = this.activatedRoute.snapshot.fragment;

    if (fragment) {
      let index = parseInt(fragment.split('_')[1]);
      if (index < this._items.length) {
        this._scrollToAnchorService.scroll(fragment);
      } else {
        this._scrollToAnchorService.scroll("row_" + (this._items.length - 1));
      }
    }
  }


  private ErrorStateEnum = CallListComponent.ErrorState;
}

export namespace CallListComponent {

  export enum ErrorState {
    /**
     * Нет ошибок
     */
    None,
    /**
     * Другая ошибка при получении данных с сервера
     */
    UnknownError
  }
}



// WEBPACK FOOTER //
// /app/SPA/src/app/client/components/call/list/call-list.component.ts